import { useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../api/admin';
import { useAsync, Banner } from './ui';
import DailyLogDoc from '../../components/DailyLogDoc';
import { shareDailyLogPdf } from '../../lib/shareDailyLog';
import { logbookToday } from '../../lib/format';

/** Paper-style daily logbook — designed to print on A4 with the institute letterhead. */
export default function DailyLogAdmin() {
  const equip = useAsync(() => adminApi.listEquipment(), [], 'equipment');
  const dailyRows = (equip.data?.rows ?? []).filter((r) => String(r['Category'] ?? 'daily') !== 'pms');
  const [equipmentId, setEquipmentId] = useState('BB-BMT-001');
  const [date, setDate] = useState(logbookToday());
  const [applied, setApplied] = useState({ equipmentId, date });
  const log = useAsync(
    () => (applied.equipmentId && applied.date ? adminApi.dailyLog(applied.equipmentId, applied.date) : Promise.resolve(null)),
    [applied],
    applied.equipmentId && applied.date ? `dailyLog:${applied.equipmentId}:${applied.date}` : undefined,
  );

  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfMsg, setPdfMsg] = useState('');

  async function sharePdf() {
    if (!log.data?.template) return;
    setPdfBusy(true); setPdfMsg('');
    try {
      const how = await shareDailyLogPdf(log.data, applied.equipmentId);
      if (how === 'downloaded') setPdfMsg('PDF downloaded.');
    } catch (e) {
      if ((e as Error)?.name !== 'AbortError') setPdfMsg('Could not create the PDF: ' + ((e as Error)?.message ?? e));
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <div className="dailyDoc">
      <div className="screen-only">
        <h1>Daily logbook</h1>
        <p className="muted tiny" style={{ marginTop: -6 }}>
          Daily-reading equipment only — PMS checklists have their own report, generated per visit
          from <Link to="/admin/readings">Readings</Link>.
        </p>
        <div className="filters">
          <div><label>Equipment</label>
            <select value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)}>
              {dailyRows.map((r, i) => (
                <option key={i} value={String(r['Equipment ID'])}>{String(r['Equipment Name'])}</option>
              ))}
            </select>
          </div>
          <div><label>Date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <button className="sm" onClick={() => setApplied({ equipmentId, date })}>Show</button>
          <button className="secondary sm" onClick={() => window.print()} disabled={!log.data}>🖨 Print (A4)</button>
          <button className="secondary sm" onClick={sharePdf} disabled={!log.data || pdfBusy}>
            {pdfBusy ? 'Preparing…' : '📤 Share PDF'}
          </button>
        </div>
        <Banner error={log.error || pdfMsg} ok={pdfMsg === 'PDF downloaded.' ? pdfMsg : undefined} />
        {log.loading && <div className="spinner" />}
      </div>

      {log.data && log.data.template && (
        <DailyLogDoc data={log.data} equipmentId={applied.equipmentId} />
      )}
    </div>
  );
}
